/**
 * Gerador de senhas seguro para browser
 */
class PasswordGenerator {
  constructor() {
    this.lowercase = 'abcdefghijklmnopqrstuvwxyz';
    this.uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    this.numbers = '0123456789';
    this.symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  }

  /**
   * Gera uma senha aleatória
   * @param {Object} options - Opções de configuração
   * @returns {string} - Senha gerada
   */
  generate(options = {}) {
    const config = {
      length: options.length || 12,
      numbers: options.numbers !== false,
      symbols: options.symbols !== false,
      lowercase: options.lowercase !== false,
      uppercase: options.uppercase !== false,
      excludeSimilarCharacters: options.excludeSimilarCharacters || false,
      exclude: options.exclude || '',
      strict: options.strict || false,
    };

    let charset = '';
    const requiredChars = [];

    // Construir charset baseado nas opções
    if (config.lowercase) {
      charset += this.lowercase;
      if (config.strict) requiredChars.push(this.getRandomChar(this.lowercase));
    }

    if (config.uppercase) {
      charset += this.uppercase;
      if (config.strict) requiredChars.push(this.getRandomChar(this.uppercase));
    }

    if (config.numbers) {
      charset += this.numbers;
      if (config.strict) requiredChars.push(this.getRandomChar(this.numbers));
    }

    if (config.symbols) {
      charset += this.symbols;
      if (config.strict) requiredChars.push(this.getRandomChar(this.symbols));
    }

    // Remover caracteres similares se solicitado
    if (config.excludeSimilarCharacters) {
      charset = charset.replace(/[il1Lo0O]/g, '');
    }

    // Remover caracteres específicos
    if (config.exclude) {
      for (const char of config.exclude) {
        const esc = char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        charset = charset.replace(new RegExp(esc, 'g'), '');
      }
    }

    if (charset.length === 0) {
      throw new Error('Charset está vazio. Verifique as configurações.');
    }

    let password = '';

    // Adicionar caracteres obrigatórios primeiro (modo strict)
    if (config.strict && requiredChars.length > 0) {
      password += requiredChars.join('');
    }

    // Preencher o restante da senha
    const remainingLength = config.length - password.length;
    for (let i = 0; i < remainingLength; i++) {
      password += this.getRandomChar(charset);
    }

    // Embaralhar a senha para evitar padrões previsíveis
    return this.shuffleString(password);
  }

  /**
   * Obtém um caractere aleatório de uma string
   * @param {string} str - String de origem
   * @returns {string} - Caractere aleatório
   */
  getRandomChar(str) {
    const randomIndex = this.getSecureRandomInt(0, str.length);
    return str.charAt(randomIndex);
  }

  /**
   * Gera um número inteiro aleatório seguro
   * @param {number} min - Valor mínimo (inclusivo)
   * @param {number} max - Valor máximo (exclusivo)
   * @returns {number} - Número aleatório
   */
  getSecureRandomInt(min, max) {
    const range = max - min;
    if (range <= 0) return min;

    const bytesNeeded = Math.max(1, Math.ceil(Math.log2(range) / 8));
    const randomBytes = new Uint8Array(bytesNeeded);

    // Usar crypto.getRandomValues se disponível, senão fallback para Math.random
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(randomBytes);
      let randomValue = 0;
      for (let i = 0; i < bytesNeeded; i++) {
        randomValue = (randomValue << 8) + randomBytes[i];
      }
      return min + (randomValue % range);
    } else {
      // Fallback para Math.random (menos seguro)
      return min + Math.floor(Math.random() * range);
    }
  }

  /**
   * Embaralha uma string
   * @param {string} str - String para embaralhar
   * @returns {string} - String embaralhada
   */
  shuffleString(str) {
    const arr = str.split('');
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.getSecureRandomInt(0, i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.join('');
  }
}

// Criar instância do gerador
const passwordGenerator = new PasswordGenerator();

// Manter compatibilidade com a API da biblioteca original
if (typeof window !== 'undefined') {
  window.GeneratePassword = {
    generate: (options) => passwordGenerator.generate(options),
  };
  // Também disponibilizar a classe diretamente
  window.PasswordGenerator = PasswordGenerator;
}

export default passwordGenerator;